/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import ContentTaskComparator from '../../comparator/content-task-comparator';

/**
 * @private
 * @typedef {Record<string, *>} ContentTaskCron TODO #555
 * @typedef {Record<string, *>} ContentTaskPlan TODO #555
 * @typedef {Record<string, *>} Timers TODO #555
 * @typedef {import('../../content-task').ContentTask} ContentTask
 * @typedef {import('../iterator/content-task-plan-iterator').default} ContentTaskPlanIterator
 * @typedef {import('../iterator/factory/content-task-plan-iterator-factory').default} ContentTaskPlanIteratorFactory
 */

// TODO Výběr a prioritizace úloh by mohlo být součástí nadřazené třídy.
// - Vyčlenit něco jako ContentTaskSelector
// - Zatím pro jednoduchost ponecháno zde v konstruktoru
// TODO Implementovat getCurrentTask bez naslouchání taskChanged
// - Pravděpodobně se musí chovat odlišně při spuštěném a při vypnutém plánování.
//   Respektive se spíše asi bude řídit obsahem currentPlan.
// - Musí se ošetřit varianta, kdy je this.iteratorResult v minulosti.
// - Když není CRON spuštěný, není možné zaručit aktuálnost this.iteratorResult
//   a getCurrentTask by tak musel provést vlastní zjišťování.
// - Pozor na to, že při zapnutém časovači je nutné se řídit podle něj
//   a jeho případného zpoždění / zrychlení.
//   V opačném případě by byly výsledky nekonzistentní a getCurrentTask
//   by mohl vracet jinou hodnotu než tu o které informoval taskChanged.
//   Bez zapnutného časovače, je možné pro zjištění úlohy použít aktuální čas.
// TODO Nerušit iterator a iteratorResult při zastavení sledování taskChanged
// - Pokud by se často měnilo naslouchání, tak lze zachovat vypočtená data.
//   Až v okamžiku, kdy je plán již neaktuální (nowTime >= endTime)
//   by se iterátor zahodil.
// - Pravděpodobně by se neměly provádět pokusy o přeskakování neaktuálních
//   úloh, vždy zahodit a vytvořit nový iterátor.
// - Asi se bude muset přidat příznak this.started pro určení stavu.
// TODO Časovače nejsou dokonalé a mají některé známé problémy:
// - V produkci se používá implementace `LongTimers`, která dovoluje nastavit
//   časovač na maximálně cca. 25 dní.
//   Pokud tedy bude v plánování obsahových úloh změna v přehrávání po době
//   delší než 25 dní, bude vyvolána po 25 dnech.
//   Následně se tedy bude přehrávat nesprávný obsah,
//   který měl být spuštěný až později.
// - Vzhledem ke způsobu jak se pracuje s časovači v JS,
//   nedochází při plánování k reakcím na změnu nastavení času na TV.
//   Když se na TV přehrává obsah a za hodinu se má změnit na jiný,
//   tak i když se posunou hodiny v nastavení o nějaký čas dopředu,
//   tak stále se změna provede až za hodinu od začátku přehrávání.
// - Není podrobně otestováno, jak se bude plánování chovat při změně času
//   mezi letním a zimním.
// - Řešením výše uvedených problémů by asi bylo přepracovat časovače na jediný,
//   který by v nějakém intervalu (cca. vteřina?) kontroloval skutečný
//   nastavený čas a podle toho by se reagovalo.
/**
 * "CRON" plánovač obsahových úloh založený na kalendářích
 *
 * Stará se o sledování plánu obsahových úloh, který definují události
 * v kalendáři.
 * Úloha se přehrává v době, kdy je aktivní alespoň jedna událost v kalendáři.
 *
 * Sleduje aktuální čas a podle něj prochází plánem obsahových úloh a informuje
 * o změnách, které v něm nastávají.
 * @implements {ContentTaskCron}
 */
class ContentTaskPlanCron {
  /**
   * @param {Array.<ContentTask>} contentTasks Úlohy, na kterých se má plánovat.
   * Na jejich pořadí nezáleží.
   * @param {ContentTaskPlanIteratorFactory} iteratorFactory
   * @param {Timers} timers
   */
  constructor(contentTasks, iteratorFactory, timers) {
    this.contentTasks = contentTasks
      .filter((task) =>
        [task.layout, task.medium, task.playlist].some((model) => !isNil(model))
      )
      // Úlohy se seřadí od nejdůležitější po nejméně důležitou.
      // Jde tedy o inverzi výchozího řazení,
      // kde je důležitější úloha na vyšším indexu.
      .sort((a, b) => ContentTaskComparator.compare(a, b) * -1);
    // TODO #555: Zavést EventBus nebo EventTypes.
    this.emitter = new EventEmitter();
    /**
     * Iterátor pro aktuálně spuštěné plánování.
     * Je nastaven na `null`, pokud je sledování změn zastaveno.
     * @private
     * @type {ContentTaskPlanIterator|null}
     */
    this.iterator = null;
    this.iteratorFactory = iteratorFactory;
    /**
     * Poslední výsledek iterátoru, který byl naplánován.
     *
     * Je nastaven na `null`, pokud je sledování změn zastaveno.
     * Pokud je sledování změn spuštěno, je výsledek buď aktivní,
     * nebo v budoucnosti.
     * Po vypnutí sledování změn může být i v minulosti.
     *
     * Pomocí hodnoty `done`, lze určit důvod, proč má úloha nastavený `null`.
     * Při nastavení na `true` je jisté, že už další úloha nepřijde.
     * V opačném případě jde jen o mezeru mezi dvěma úlohami.
     * @private
     * @type {IteratorResult.<ContentTaskPlan>|null}
     */
    this.iteratorResult = null;
    /**
     * Časovač reakce na následující začátek nebo konec plánu.
     * Je nastaven na `null`, pokud je plánování zastaveno, nebo pokud již žádný
     * další plán není.
     * @private
     * @type {null|number}
     */
    this.timeout = null;
    this.timers = timers;
  }

  /** @inheritdoc */
  getCurrentTask() {
    const { iteratorResult } = this;
    // Aktivní sledování změn zaručuje aktuální `iteratorResult`.
    // TODO Implementovat getCurrentTask bez naslouchání taskChanged (viz. nahoře)
    if (isNil(iteratorResult)) {
      throw new Error('CRON is not started.');
    }
    // Pokud již plán skončil není žádná úloha aktivní.
    if (iteratorResult.done) {
      return null;
    }
    const plan = iteratorResult.value;
    const nowTime = Date.now();
    const startDelay = plan.startDate.getTime() - nowTime;
    // Pokud plán ještě nezačal, není žádná úloha aktivní.
    if (startDelay > 0) {
      return null;
    }
    // Vrátí se aktivní úloha.
    return plan.contentTask;
  }

  /** @inheritdoc */
  off(type, callback) {
    // Zruší se naslouchání události.
    const { emitter } = this;
    emitter.off(type, callback);
    // Pokud šlo o poslední naslouchání události, zastaví se sledování.
    if (type === 'taskChanged' && emitter.listenerCount('taskChanged') === 0) {
      this.stop();
    }
  }

  /** @inheritdoc */
  on(type, callback) {
    const { emitter } = this;
    // Pokud jde o první naslouchání události, zahájí se sledování.
    if (type === 'taskChanged' && emitter.listenerCount('taskChanged') === 0) {
      this.start();
    }
    // Nastaví se naslouchání události.
    emitter.on(type, callback);
  }

  /**
   * Rekace na konec plánu obsahové úlohy
   * @param {ContentTaskPlan} plan
   * @private
   * @returns {undefined}
   */
  onPlanEnd(plan) {
    // Zjistí se skutečný aktuální čas, ve který byl callback aktivován.
    // Nesmí se převzít ten z `plan.endDate`, kde je uložena informace,
    // kdy měla být reakce vyvolána.
    // Vzhledem k omezení časovačů, nejde o shodné časy.
    const nowTime = Date.now();
    // Naplánuje se následující plán obsahových úloh.
    // @ts-ignore TODO #555: Ošetřit nullovitost.
    const iteratorResult = this.iterator.next();
    this.iteratorResult = iteratorResult;
    // Bez plánu se pouze informuje o tom, že není aktivní žádná událost
    // a již nic dále se nedělá.
    if (iteratorResult.done) {
      this.timeout = null;
      this.emitter.emit('taskChanged', null);
      return;
    }
    // Pokud se plán našel, je třeba rozhodnout, jestli těsně navazuje na
    // aktuální plán, nebo je v budoucnosti.
    const nextPlan = iteratorResult.value;
    const nextStartTime = nextPlan.startDate.getTime();
    const nextTask = nextPlan.contentTask;
    const { timers } = this;
    // Pokud plán těsně navazuje, naplánuje se jeho konec.
    // Událost se vyvolá pouze, pokud došlo ze změně úlohy na jinou.
    // Opakované změny na tu stejnou úlohu bez přerušení se ignorují.
    if (plan.endDate.getTime() === nextStartTime) {
      const delay = nextPlan.endDate.getTime() - nowTime;
      this.timeout = timers.setTimeout(() => this.onPlanEnd(nextPlan), delay);
      if (plan.contentTask !== nextTask) {
        this.emitter.emit('taskChanged', nextTask);
      }
      return;
    }
    // Pokud je plán v budoucnosti, naplánuje se reakce na jeho začátek
    // a vyvolá se událost, že nyní není aktivní žádná událost.
    const delay = nextStartTime - nowTime;
    this.timeout = timers.setTimeout(() => this.onPlanStart(nextPlan), delay);
    this.emitter.emit('taskChanged', null);
  }

  /**
   * Rekace na začátek plánu obsahové úlohy
   * @param {ContentTaskPlan} plan
   * @private
   * @returns {void}
   */
  onPlanStart(plan) {
    // Zjistí se skutečný aktuální čas, ve který byl callback aktivován.
    // Nesmí se převzít ten z `plan.startDate`, kde je uložena informace,
    // kdy měla být reakce vyvolána.
    // Vzhledem k omezení časovačů, nejde o shodné časy.
    const nowTime = Date.now();
    // Naplánuje se časovač na reakci na konec aktivní události.
    const delay = plan.endDate.getTime() - nowTime;
    this.timeout = this.timers.setTimeout(() => this.onPlanEnd(plan), delay);
    // Vyvolá se událost informující o změně aktivní úlohy.
    this.emitter.emit('taskChanged', plan.contentTask);
  }

  /**
   * Zahájí sledování změn v plánu od aktuálního okamžiku.
   * O změnách pak informuje pomocí vyvolání událostí.
   * @fires ContentTaskCron#taskChanged
   * @private
   * @returns {undefined}
   */
  start() {
    if (
      [this.iterator, this.iteratorResult, this.timeout].some(
        (value) => !isNil(value)
      )
    ) {
      throw new Error('CRON has already started.');
    }
    // Zjistí se aktuální čas, pro který se má začít plánovat.
    const nowDate = new Date();
    const nowTime = nowDate.getTime();
    // Na základě aktuálního času se vytvoří iterátor plánů,
    // který bude postupně procházet potenciálně nekonečnou řadu plánů.
    // Iterátor se vytváří až zde, protože tato třída má jako jediná přístup
    // k aktuálnímu času.
    // Také je díky tomu možné opakovaně zastavovat a spouštět plánování.
    const iterator = this.iteratorFactory.create(nowDate, this.contentTasks);
    this.iterator = iterator;
    // Provede se přechod na první plán.
    const iteratorResult = iterator.next();
    this.iteratorResult = iteratorResult;
    // Bez plánu se pouze informuje o tom, že není aktivní žádná událost
    // a již nic dále se nedělá.
    if (iteratorResult.done) {
      return;
    }
    // Pokud se plán našel, je třeba rozhodnout, jestli je již aktivní,
    // nebo je v budoucnosti.
    const plan = iteratorResult.value;
    const startDelay = plan.startDate.getTime() - nowTime;
    const { timers } = this;
    // Pokud je plán v budoucnosti, naplánuje se reakce na jeho začátek
    // a vyvolá se událost, že nyní není aktivní žádná událost.
    if (startDelay > 0) {
      this.timeout = timers.setTimeout(
        () => this.onPlanStart(plan),
        startDelay
      );
      return;
    }
    // Pokud je plán již nyní aktivní, naplánuje se reakce na konec
    // a vyvolá se událost s již aktivní události.
    const endDelay = plan.endDate.getTime() - nowTime;
    this.timeout = timers.setTimeout(() => this.onPlanEnd(plan), endDelay);
  }

  /**
   * Zastaví sledování změn v plánu od aktuálního okamžiku.
   * Tuto metodu je nutné zavolat před zrušením odkazů na plánovač,
   * jinak není zaručeno uvolnění paměti během garbage-collecting.
   * @private
   * @returns {void}
   */
  stop() {
    this.timers.clearTimeout(this.timeout);
    this.iterator = null;
    this.iteratorResult = null;
    this.timeout = null;
  }
}

export default ContentTaskPlanCron;
